import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface UseAssetTypeHasLengthArgs {
	systemType?: SystemTypeId;
	assetType?: AssetTypeId;
}

/**
 * Hook to determine if an asset type has a length property based purely on the asset schema from Redux
 * Uses getFlatProperties() to check for a property with id 'length'
 * Returns false if schema is not available or doesn't have getFlatProperties method
 * @param systemType - The system type ID
 * @param assetType - The asset type ID
 * @returns boolean indicating if the asset type has a length property according to the schema
 */
export function useAssetTypeHasLength({
	systemType,
	assetType,
}: UseAssetTypeHasLengthArgs): boolean {
	const dispatch = useDispatch();

	useEffect(() => {
		if (systemType && assetType) {
			dispatch(
				getAssetSchemaAction({
					systemType: systemType.id,
					assetType: assetType.id,
				}),
			);
		}
	}, [dispatch, systemType, assetType]);

	const schema = useSelector(state => {
		if (assetType) {
			return assetSchemaSelectorByType(state, assetType.id);
		}
	});

	// If we don't have the required parameters or schema, return false
	if (!systemType || !assetType || !schema) {
		return false;
	}

	const hasLengthProperty = !!schema.getFlatProperty('length');

	return hasLengthProperty;
}
